// src/components/Prospecting/SiteConsultants/SiteConsultantsGrid.js

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import BrandSpinner from "../../../shared/components/BrandSpinner";

import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import TwitterIcon from "@mui/icons-material/Twitter";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import { getConsultantIndustries } from "./SiteConsultantHelpers";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

// -------------------------------
// Expandable Green Icon Chip
// -------------------------------
function IconChip({ icon, label, expanded, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: BRAND_GREEN,
        color: "white",
        borderRadius: "18px",
        height: 36,
        minWidth: 36,
        px: expanded ? 1.2 : 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: expanded ? 0.8 : 0,
        cursor: "pointer",
        transition: "all 0.2s ease",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
    >
      {icon}
      {expanded && <span>{label}</span>}
    </Box>
  );
}

export default function SiteConsultantsGrid({
  consultants,
  loading,
  selectedIds,
  toggleSelect,
  revealedState,
  toggleReveal,
  flippedCards,
  toggleFlip,
}) {
  if (loading && consultants.length === 0) {
    return (
      <BrandSpinner compact py={8} />
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(auto-fit, minmax(260px, 280px))",
        },
        gap: { xs: 2, sm: 2.5, md: 3 },
        width: "100%",
        justifyContent: "center",
        justifyItems: "center",
        pb: 3,
      }}
    >
      {consultants.map((c) => {
        const reveal = revealedState[c.dynamics_id] || {};
        const isFlipped = flippedCards[c.dynamics_id];

        const industries = getConsultantIndustries(c);

        return (
          <Box
            key={c.dynamics_id}
            sx={{
              width: { xs: "100%", sm: 280 },
              maxWidth: 280,
              height: 340,
              perspective: "1000px",
              position: "relative",
            }}
          >
            <Box
              onClick={() => toggleFlip(c.dynamics_id)}
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                transition: "transform 0.6s",
                transformStyle: "preserve-3d",
                transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                cursor: "pointer",
              }}
            >
              {/* ================= FRONT ================= */}
              <Card
                sx={{
                  position: "absolute",
                  inset: 0,
                  backfaceVisibility: "hidden",
                  height: 340,
                  width: "100%",
                  borderRadius: 3,
                  boxShadow: 3,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  border: selectedIds.has(c.dynamics_id)
                    ? `3px solid ${BRAND_GREEN}`
                    : `2px solid ${BRAND_BLUE}`,
                  transition: "all 0.2s ease",

                  "&:hover .select-circle": {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  className="select-circle"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelect(c.dynamics_id);
                  }}
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `2px solid ${BRAND_GREEN}`,
                    backgroundColor: selectedIds.has(c.dynamics_id)
                      ? BRAND_GREEN
                      : "white",
                    opacity: selectedIds.has(c.dynamics_id) ? 1 : 0,
                    transition:
                      "opacity 0.15s ease, background-color 0.15s ease",
                    cursor: "pointer",
                    zIndex: 5,
                  }}
                />

                <CardContent
                  sx={{
                    p: 2,
                    pt: 3,
                    pl: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {/* TOP INFO */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        color: BRAND_BLUE,
                        textAlign: "center",
                        width: "100%",
                        pr: 3,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {c.name}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.2,
                        mt: 1,
                        width: "100%",
                      }}
                    >
                      {c.company && (
                        <Tooltip title={c.company} arrow>
                          <Chip
                            label={c.company}
                            size="small"
                            sx={{
                              width: "92%",
                              height: 30,
                              backgroundColor: BRAND_BLUE,
                              color: "white",
                              fontWeight: 600,
                              justifyContent: "center",
                              "& .MuiChip-label": {
                                width: "100%",
                                textAlign: "center",
                                fontSize: "0.85rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                          />
                        </Tooltip>
                      )}

                      {c.title && (
                        <Chip
                          label={c.title}
                          size="small"
                          sx={{
                            width: "92%",
                            height: 30,
                            backgroundColor: "#eef3fa",
                            color: BRAND_BLUE,
                            fontWeight: 500,
                            justifyContent: "center",
                            "& .MuiChip-label": {
                              width: "100%",
                              textAlign: "center",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                      )}

                      {c.location && (
                        <Chip
                          label={c.location}
                          size="small"
                          sx={{
                            width: "92%",
                            height: 30,
                            backgroundColor: "#e7f0da",
                            color: BRAND_GREEN,
                            justifyContent: "center",
                            "& .MuiChip-label": {
                              width: "100%",
                              textAlign: "center",
                              fontSize: "0.85rem",
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* CONTACT ICON CHIPS */}
                  <Box
                    sx={{
                      mt: "auto",
                      pt: 0.5,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.65,
                    }}
                  >
                    {c.linkedin && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconChip
                          icon={<LinkedInIcon fontSize="medium" />}
                          label={c.linkedin}
                          expanded={reveal.linkedin}
                          onClick={() =>
                            toggleReveal(c.dynamics_id, "linkedin")
                          }
                        />
                      </Box>
                    )}

                    {c.twitter && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconChip
                          icon={<TwitterIcon fontSize="medium" />}
                          label={c.twitter}
                          expanded={reveal.twitter}
                          onClick={() => toggleReveal(c.dynamics_id, "twitter")}
                        />
                      </Box>
                    )}

                    {c.website && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconChip
                          icon={<LanguageIcon fontSize="medium" />}
                          label={c.website}
                          expanded={reveal.website}
                          onClick={() => toggleReveal(c.dynamics_id, "website")}
                        />
                      </Box>
                    )}

                    {c.email && (
                      <Box
                        component="a"
                        href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ textDecoration: "none" }}
                      >
                        <IconChip icon={<EmailIcon fontSize="medium" />} />
                      </Box>
                    )}

                    {c.office_phone && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconChip
                          icon={<PhoneIcon fontSize="medium" />}
                          label={c.office_phone}
                          expanded={reveal.office}
                          onClick={() => toggleReveal(c.dynamics_id, "office")}
                        />
                      </Box>
                    )}

                    {c.mobile_phone && (
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconChip
                          icon={<SmartphoneIcon fontSize="medium" />}
                          label={c.mobile_phone}
                          expanded={reveal.mobile}
                          onClick={() => toggleReveal(c.dynamics_id, "mobile")}
                        />
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* ================= BACK ================= */}
              <Card
                sx={{
                  position: "absolute",
                  inset: 0,
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  height: 340,
                  width: 280,
                  borderRadius: 3,
                  boxShadow: 3,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  border: `2px solid ${BRAND_BLUE}`,
                  background:
                    "linear-gradient(180deg, rgba(231,240,218,0.9), white)",
                }}
              >
                <CardContent
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: BRAND_BLUE,
                      mb: 1,
                      textAlign: "center",
                    }}
                  >
                    Industries
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {industries.length ? (
                      industries.map((ind, i) => (
                        <Chip
                          key={i}
                          label={ind}
                          size="small"
                          sx={{
                            backgroundColor: BRAND_BLUE,
                            color: "white",
                            width: "100%",
                          }}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          textAlign: "center",
                        }}
                      >
                        No industries listed
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    sx={{
                      mt: "auto",
                      pt: 1.5,
                      fontSize: 12,
                      color: "text.secondary",
                      textAlign: "center",
                      opacity: 0.75,
                    }}
                  >
                    Click to flip back
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
